import { makeStyles, Step, StepButton, StepIconProps, StepLabel, Stepper } from '@material-ui/core';
import { useEffect, useState } from 'react';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import Tooltip from 'react-bootstrap/esm/Tooltip';
import { IQuestionnaireWL } from '../../container/Fragebogen/QuestionnaireWL';
import { pcpTheme } from '../../globals/theme';
import { useLanguage } from '../../hooks/useLanguage';
import useScreenResolution from '../../hooks/useScreenResolution';
import './Stepper.css';

export enum StepperOrientation {
    Horizontal,
    Vertical,
}

export enum StepperLabelOrientation {
    Right,
    Bottom,
}

export enum StepTyp {
    Normal = 'NORMAL',
    Journey = 'JOURNEY',
}

export interface IStepperCheckInStep {
    id: string;
    name: string;
    typ: StepTyp;
    completed: boolean;
    draft: boolean;
    active: boolean;
    clickable: boolean;
    url?: string;
    hashParams?: IQuestionnaireWL;
}

export interface IStepperCheckIn {
    steps: IStepperCheckInStep[];
    backgroundColor?: string;
    handleStepClick(stepNumberClicked: number): void;
    stepperOrientation: StepperOrientation;
    labelOrientation: StepperLabelOrientation;
    paddingTop?: number | string;
    paddingBottom?: number | string;
}

const useStyles = makeStyles({
    root: {
        '& .MuiStepIcon-root': { color: 'gray' },
        '& .MuiStepIcon-completed': { color: pcpTheme.step.completed + ' !important' },
        '& .MuiStepIcon-active': { color: pcpTheme.step.active + ' !important' } /*{ color: 'rgb(36, 94, 173)' },*/,
        '& .MuiStepIcon-text': { fill: 'black' },

        '& .Mui-disabled .MuiStepIcon-root': { color: '#bab9b9 !important' },
        // '& .MuiStepLabel-alternativeLabel': { marginTop: '10px', marginBottom: '30px', color: 'white', width: '100%' },
        '& .MuiStep-alternativeLabel': { overflow: 'clip', minWidth: '0px', overflowClipMargin: 'content-box 500px' },
        '& .MuiStepLabel-alternativeLabel': { overflow: 'hidden' },
        '& .MuiStepLabel-label': {
            color: 'black',
            textAlign: 'left',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
        },
        '& .MuiStepLabel-active .MuiStepLabel-label': {
            fontWeight: '530',
        },
        '& .MuiStepConnector-lineVertical': { minHeight: '60px', borderColor: pcpTheme.step.line },
        '& .MuiStepConnector-horizontal': {
            visibility: 'visible',
            position: 'absolute',
            borderColor: pcpTheme.step.line,
        },
        '& .MuiStepConnector-lineHorizontal': {
            visibility: 'visible !important',
            position: 'static',
            borderColor: pcpTheme.step.line,
        },
    },
    stepButtonDraft: {
        '& .MuiStepIcon-root': { color: '#0080009c' },
    },
});

const CustomStepIcon: React.FC<StepIconProps> = ({ active, completed, icon }) => {
    const bg = completed ? pcpTheme.step.completed : active ? pcpTheme.step.active : '#bab9b9';

    return (
        <span
            style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: bg,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {completed ? (
                // Schwarzer Haken
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" fill="black" />
                </svg>
            ) : (
                // Step-Nummer in schwarz
                <span style={{ color: 'black', fontSize: 12, lineHeight: 1 }}>{icon}</span>
            )}
        </span>
    );
};

const StepperCheckIn = (props: IStepperCheckIn) => {
    const { t } = useLanguage();
    const classes = useStyles();

    const [activeStep, setActiveStep] = useState(0);
    const [completed, setCompleted] = useState({});
    const [steps, setSteps] = useState<IStepperCheckInStep[]>([
        {
            id: '',
            name: '',
            typ: StepTyp.Normal,
            completed: false,
            draft: false,
            active: false,
            clickable: false,
            url: '',
        },
    ]);

    const screenSize = useScreenResolution();

    // const steps = ['Select campaign settings', 'Create an ad group', 'Create an ad'];

    const haveStepsChanged = () => {
        let isSame = false;
        let currentSteps;
        let newSteps;

        if (props.steps) {
            currentSteps = steps.map((step) => step.id);
            newSteps = props.steps.map((step) => step.id);
        }

        isSame =
            currentSteps.length === newSteps.length && currentSteps.every((value, index) => value === newSteps[index]);
        return !isSame;
    };

    useEffect(() => {
        let newCompleted = completed;
        let activeIndex = 0;

        if (props.steps) {
            if (haveStepsChanged()) {
                newCompleted = {};
                setActiveStep(0);
            }
            if (props.steps.length > 0) {
                props.steps.forEach((step, index) => {
                    if (step.active) {
                        activeIndex = index;
                    }
                    if (step.completed) {
                        newCompleted[index] = true;
                    }
                });
            }
            setCompleted(newCompleted);
            setActiveStep(activeIndex);

            setSteps(props.steps);
        }
    }, [props.steps]);

    const totalSteps = () => {
        return steps.length;
    };

    const completedSteps = () => {
        return Object.keys(completed).length;
    };

    const allStepsCompleted = () => {
        return completedSteps() === totalSteps();
    };

    const handleStep = (step) => () => {
        setActiveStep(step);
        props.handleStepClick(step);
    };

    // const handleComplete = () => {
    //     const newCompleted = completed;
    //     newCompleted[activeStep] = true;
    //     setCompleted(newCompleted);
    // };

    const getTooltipText = (isCompleted: boolean, isDraft: boolean) => {
        let text = 'noch offen';
        if (isCompleted) {
            text = 'abgeschlossen';
        } else if (isDraft) {
            text = 'zwischengespeichert';
        }
        return text;
    };

    const getStepperOrientation = () => {
        if (props.stepperOrientation === StepperOrientation.Vertical) {
            return 'vertical';
        } else {
            return 'horizontal';
        }
    };

    const getStepperLabelOrientation = () => {
        if (props.labelOrientation === StepperLabelOrientation.Right) {
            return false;
        } else {
            return true;
        }
    };

    return (
        <>
            <Stepper
                nonLinear
                activeStep={activeStep}
                orientation={getStepperOrientation()}
                alternativeLabel={getStepperLabelOrientation()}
                style={{
                    backgroundColor: props.backgroundColor ? props.backgroundColor : 'white',
                    width: '100%',
                    paddingRight: '10px',
                    paddingTop: props.paddingTop ? props.paddingTop : '25px',
                    paddingBottom: props.paddingBottom ? props.paddingBottom : '25px',
                    paddingLeft: screenSize.width < 500 ? '0px' : '24px',
                }}
                className={classes.root}
            >
                {steps.map((label, index, origArray) => (
                    <Step key={label.name} completed={completed[index]}>
                        <StepButton
                            classes={{ root: label.draft ? classes.stepButtonDraft : classes.root }}
                            onClick={handleStep(index)}
                            disabled={!label.clickable}
                            aria-label={t('ANMELD', 'Schritt {{stepNum}} von {{totalStepNum}}: {{label}}', {
                                stepNum: (index + 1).toString(),
                                totalStepNum: origArray.length.toString(),
                                label: label.name,
                            })}
                        >
                            <StepLabel StepIconComponent={CustomStepIcon} style={{ wordBreak: 'break-word' }}>
                                <OverlayTrigger
                                    key={'overlay_' + label.name}
                                    placement="bottom"
                                    overlay={
                                        <Tooltip id={`tooltip-${label.name}`}>
                                            {getTooltipText(completed[index], label.draft)}
                                        </Tooltip>
                                    }
                                >
                                    <span>{label.name}</span>
                                </OverlayTrigger>
                            </StepLabel>
                        </StepButton>
                    </Step>
                ))}
            </Stepper>
            <div>{allStepsCompleted() ? <>{/* <p>Alles erledigt</p> */}</> : <></>}</div>
        </>
    );
};

export default StepperCheckIn;
